import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';

function AccessibilityStatement() {
    useEffect(() => { window.scrollTo(0, 0); }, []);

    return (
        <main className="privacy-page">
            <Helmet>
                <title>Accessibility Statement — SRL Advisor</title>
                <meta name="description" content="SRL Advisor accessibility statement. Designed to conform to WCAG 2.2 Level AA. Keyboard navigation, screen reader support, and how to report issues." />
                <link rel="canonical" href="https://srladvisor.com/accessibility" />
            </Helmet>
            <Container className="privacy-container">
                <Link to="/" className="privacy-back">&larr; Back to home</Link>
                <h1 className="privacy-title">Accessibility Statement</h1>
                <p className="privacy-effective">Last updated: July 2026</p>

                <section className="privacy-section">
                    <h2>About this statement</h2>
                    <p>
                        This accessibility statement applies to the SRL Advisor website
                        (srladvisor.com) and the SRL Advisor platform (the Moodle plugin
                        and its supporting web application). SRL Advisor is a doctoral
                        research project at the University of St&nbsp;Andrews, led by
                        Andrew Schwabe.
                    </p>
                    <p>
                        We want as many people as possible to be able to use SRL Advisor.
                        If you experience any accessibility barriers, please contact us
                        at{' '}
                        <a href="mailto:hello@srladvisor.com">hello@srladvisor.com</a>.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Compliance status</h2>
                    <p>
                        SRL Advisor has been designed to conform to the{' '}
                        <a href="https://www.w3.org/TR/WCAG22/" target="_blank" rel="noopener noreferrer">
                            Web Content Accessibility Guidelines (WCAG) 2.2
                        </a>{' '}
                        Level AA standard. This applies to both the public website and
                        the student-facing platform. A formal accessibility audit has not
                        yet been completed; we will update this statement with the results
                        when one is conducted.
                    </p>
                    <p>
                        We assess our approach against the Public Sector Bodies (Websites
                        and Mobile Applications) (No.&nbsp;2) Accessibility
                        Regulations 2018, which require WCAG 2.2 AA conformance for
                        public sector web content in the United Kingdom.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>What you should be able to do</h2>
                    <p>You should be able to:</p>
                    <ul>
                        <li>
                            Navigate the entire website and platform using only a keyboard.
                        </li>
                        <li>
                            Navigate the website and platform using screen reader software
                            (including JAWS, NVDA, and VoiceOver).
                        </li>
                        <li>
                            Zoom in up to 400% without content overflowing or requiring
                            horizontal scrolling.
                        </li>
                        <li>
                            Change colours, contrast levels, and fonts using browser
                            settings or assistive technology.
                        </li>
                        <li>
                            Use speech recognition software (such as Dragon
                            NaturallySpeaking) to interact with all controls.
                        </li>
                        <li>
                            Complete all surveys, check-ins, and platform interactions
                            without relying on colour alone to convey information.
                        </li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>Accessibility features</h2>

                    <h3>Semantic HTML and ARIA</h3>
                    <p>
                        Pages use semantic HTML elements (headings, landmarks, lists,
                        forms) to provide a meaningful document structure. Where custom
                        interactive components are used, appropriate ARIA roles, states,
                        and properties are applied to ensure assistive technology
                        compatibility.
                    </p>

                    <h3>Keyboard navigation</h3>
                    <p>
                        All interactive elements (links, buttons, form fields, and
                        navigation controls) are reachable and operable via keyboard.
                        Focus indicators are visible on all interactive elements.
                    </p>

                    <h3>Colour and contrast</h3>
                    <p>
                        Text and interactive elements meet or exceed the WCAG 2.2 AA
                        minimum contrast ratios (4.5:1 for normal text, 3:1 for large
                        text and UI components). Information is never conveyed by colour
                        alone.
                    </p>

                    <h3>Responsive design</h3>
                    <p>
                        The website and platform are designed to reflow to a single column
                        at narrow viewport widths. Content remains readable and functional
                        at up to 400% zoom without loss of information or horizontal
                        scrolling.
                    </p>

                    <h3>Form accessibility</h3>
                    <p>
                        All form inputs have visible labels and programmatic associations.
                        Required fields are indicated both visually and programmatically.
                        Error messages identify the field in error and describe how to
                        correct the issue.
                    </p>

                    <h3>Motion and animation</h3>
                    <p>
                        Scroll-triggered animations on the website
                        respect the <code>prefers-reduced-motion</code> media query.
                        Users who have configured their operating system to reduce motion
                        will not see animated transitions.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Known limitations</h2>
                    <p>
                        We are committed to identifying and resolving accessibility
                        issues. The following limitations are currently known:
                    </p>
                    <ul>
                        <li>
                            <strong>Third-party content:</strong> the contact form is
                            processed by HubSpot. While the form itself is built with
                            accessible markup, the HubSpot tracking cookie is set without
                            a consent mechanism. We are evaluating options to address this.
                        </li>
                        <li>
                            <strong>Moodle integration:</strong> the SRL Advisor Moodle
                            plugin renders within the Moodle LMS interface. Accessibility
                            within the Moodle shell (navigation, headers, menus) is
                            governed by the institution's Moodle theme and configuration,
                            which is outside our direct control. The plugin's own content
                            and interactive elements are designed to WCAG 2.2 AA.
                        </li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>Testing</h2>
                    <p>
                        Accessibility testing is conducted using a combination of automated
                        tools and manual review. Automated checks are performed during
                        development. Manual testing includes keyboard-only navigation,
                        screen reader testing (VoiceOver on macOS), and zoom/reflow
                        testing at 400%.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Feedback and reporting issues</h2>
                    <p>
                        If you find any accessibility barriers that are not listed on this
                        page, or if you need information in a different format, please
                        contact:
                    </p>
                    <p>
                        Andrew Schwabe<br />
                        <a href="mailto:hello@srladvisor.com">hello@srladvisor.com</a>
                    </p>
                    <p>We will respond to accessibility feedback as soon as possible.</p>
                </section>

                <section className="privacy-section">
                    <h2>Enforcement procedure</h2>
                    <p>
                        The Equality and Human Rights Commission (EHRC) is responsible for
                        enforcing the Public Sector Bodies (Websites and Mobile
                        Applications) (No.&nbsp;2) Accessibility Regulations 2018. If
                        you are not satisfied with how we respond to your complaint, you
                        can contact the{' '}
                        <a href="https://www.equalityadvisoryservice.com/" target="_blank" rel="noopener noreferrer">
                            Equality Advisory and Support Service (EASS)
                        </a>.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Preparation of this statement</h2>
                    <p>
                        This statement was prepared in July 2026. It will be reviewed and
                        updated at least annually, or whenever significant changes are
                        made to the website or platform.
                    </p>
                </section>
            </Container>
        </main>
    );
}

export default AccessibilityStatement;
